import { inject, Service } from '@angular/core';
import { ClientHttpService } from '@data-services/http/client-http.service';
import { Door, DoorDetails } from '@models/doors';
import { FieldHeader } from '@models/companies';
import { getFileNameFromContentDisposition } from '@utils';
import { MountHardwareRequest } from './mount-hardware-request.model';
import { PrintDetailsMountedCountsRequest, PrintDetailsMountedRequest } from './print-details-mounted-request.model';
import { PrintDoorReportRequest } from './print-door-report-request.model';
import { PrintDoorReportResponse } from './print-door-report-response.model';

@Service()
export class DoorDataService {
  private readonly http = inject(ClientHttpService);

  async getDoorsAsync(projectId: string): Promise<Door[]> {
    return await this.http.getObjectAsync<Door[]>(`projects/${projectId}/doors`);
  }

  async getDoorFieldHeadersAsync(mbeProjectId: string): Promise<FieldHeader[]> {
    return await this.http.getObjectAsync<FieldHeader[]>(`mbeHeaders/${mbeProjectId}`);
  }

  async getGenericDoorFieldHeadersAsync(): Promise<FieldHeader[]> {
    return await this.http.getObjectAsync<FieldHeader[]>('mbeHeaders');
  }

  async getDoorDetailsAsync(projectId: string, companyId: string, doorId: string): Promise<DoorDetails> {
    return await this.http.getObjectAsync<DoorDetails>(`projects/${projectId}/company/${companyId}/doors/${doorId}/details`);
  }

  async updateHardwareMountedAsync(projectId: string, userId: string, companyId: string, doorId: string, requests: MountHardwareRequest[]): Promise<void> {
    await this.http.putObjectAsync<MountHardwareRequest[], void>(
      `projects/${projectId}/user/${userId}/company/${companyId}/doors/${doorId}/hardware/mounters/mounted`, requests);
  }

  async printDoorReportAsync(companyId: string, doorId: string, request: PrintDoorReportRequest, fallbackFileName: string): Promise<PrintDoorReportResponse> {
    return await this.postPdfAsync(`company/${companyId}/doors/${doorId}/pdf`, request, fallbackFileName);
  }

  async printDetailsMountedReportAsync(projectId: string, companyId: string, request: PrintDetailsMountedRequest, fallbackFileName: string): Promise<PrintDoorReportResponse> {
    return await this.postPdfAsync(`projects/${projectId}/company/${companyId}/doors/detailsMounted/pdf`, request, fallbackFileName);
  }

  async printDetailsMountedCountsReportAsync(projectId: string, companyId: string, request: PrintDetailsMountedCountsRequest, fallbackFileName: string): Promise<PrintDoorReportResponse> {
    return await this.postPdfAsync(`projects/${projectId}/company/${companyId}/doors/detailsMountedCounts/pdf`, request, fallbackFileName);
  }

  private async postPdfAsync(url: string, request: PrintDoorReportRequest, fallbackFileName: string): Promise<PrintDoorReportResponse> {
    const response = await this.http.postBlobAsync<PrintDoorReportRequest>(url, request);
    const blob = response.body ?? new Blob();
    const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFileName);
    return { blob, fileName };
  }
}
